import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Package, Heart, LogOut, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, RON } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { v: "profile", l: "Profil", icon: User },
  { v: "orders", l: "Comenzi", icon: Package },
  { v: "wishlist", l: "Favorite", icon: Heart },
];

export default function Account() {
  const { user, loading, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!loading && !user) navigate("/autentificare");
  }, [loading, user, navigate]);

  if (!user) return <div className="pt-40 pb-40 text-center text-muted-foreground">Se încarcă...</div>;

  return (
    <div className="pt-20">
      <section className="bg-ink text-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
          <p className="overline text-sand">Contul meu</p>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-2">Salut, {user.name?.split(" ")[0] || "prieten"}!</h1>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="h-fit space-y-2">
          {TABS.map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`account-tab-${t.v}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                tab === t.v ? "bg-ink text-cream" : "hover:bg-stone"
              }`}
            >
              <t.icon size={17} /> {t.l}
            </button>
          ))}
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-roast hover:bg-stone transition-colors"
          >
            <LogOut size={17} /> Deconectare
          </button>
        </aside>

        <div>
          {tab === "profile" && <ProfileTab user={user} updateProfile={updateProfile} />}
          {tab === "orders" && <OrdersTab />}
          {tab === "wishlist" && <WishlistTab />}
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ user, updateProfile }) {
  const [form, setForm] = useState({
    name: user.name || "", phone: user.phone || "", address: user.address || "",
    city: user.city || "", county: user.county || "", newsletter: user.newsletter || false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile(form);
      toast.success("Profil actualizat");
    } catch {
      toast.error("Nu am putut salva.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-xl space-y-5" data-testid="profile-form">
      <h2 className="font-display text-4xl mb-6">Detalii personale</h2>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Nume" value={form.name} onChange={set("name")} testid="profile-name" />
        <Field label="Telefon" value={form.phone} onChange={set("phone")} testid="profile-phone" />
      </div>
      <Field label="Adresă livrare" value={form.address} onChange={set("address")} testid="profile-address" />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Oraș" value={form.city} onChange={set("city")} testid="profile-city" />
        <Field label="Județ" value={form.county} onChange={set("county")} testid="profile-county" />
      </div>
      <label className="flex items-center gap-3 text-sm cursor-pointer">
        <input type="checkbox" checked={form.newsletter} onChange={(e) => setForm((f) => ({ ...f, newsletter: e.target.checked }))} data-testid="profile-newsletter" className="accent-roast h-4 w-4" />
        Abonează-mă la newsletter cu oferte și noutăți
      </label>
      <button type="submit" disabled={busy} data-testid="profile-save" className="bg-ink text-cream px-8 py-4 hover:bg-roast transition-colors text-sm font-semibold disabled:opacity-60">
        {busy ? "Se salvează..." : "Salvează modificările"}
      </button>
    </form>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data));
  }, []);

  if (!orders) return <p className="text-muted-foreground">Se încarcă...</p>;
  if (orders.length === 0)
    return (
      <div className="py-16 text-center" data-testid="no-orders">
        <Package size={40} className="mx-auto text-sand" />
        <p className="text-muted-foreground mt-4">Nu ai nicio comandă încă.</p>
        <Link to="/produse" className="inline-block mt-6 overline text-roast border-b border-roast pb-1">Descoperă cafeaua</Link>
      </div>
    );

  return (
    <div className="space-y-6">
      <h2 className="font-display text-4xl mb-4">Comenzile mele</h2>
      {orders.map((o) => (
        <div key={o.id} className="border border-line p-6" data-testid={`order-${o.order_number}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4">
            <div>
              <p className="overline text-roast">{o.order_number}</p>
              <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("ro-RO")}</p>
            </div>
            <span className="text-xs px-3 py-1 bg-stone">{o.status}</span>
          </div>
          <div className="divide-y divide-line">
            {o.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-4 py-3 text-sm">
                <img src={i.image} alt={i.name} className="h-12 w-10 object-cover bg-stone" />
                <div className="flex-1">
                  <p className="font-medium">{i.name}</p>
                  <p className="text-muted-foreground text-xs">{i.brand} · {i.grammage} · {i.quantity} buc.</p>
                </div>
                <span>{RON(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between pt-4">
            <span className="overline">Total</span>
            <span className="font-display text-2xl">{RON(o.total)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function WishlistTab() {
  const { toggleWishlist } = useAuth();
  const [items, setItems] = useState(null);
  const load = () => api.get("/wishlist").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const removeItem = async (slug) => {
    await toggleWishlist(slug);
    setItems((prev) => prev.filter((p) => p.slug !== slug));
    toast.message("Șters din favorite");
  };

  if (!items) return <p className="text-muted-foreground">Se încarcă...</p>;
  if (items.length === 0)
    return (
      <div className="py-16 text-center" data-testid="no-wishlist">
        <Heart size={40} className="mx-auto text-sand" />
        <p className="text-muted-foreground mt-4">Nu ai produse favorite.</p>
        <Link to="/produse" className="inline-block mt-6 overline text-roast border-b border-roast pb-1">Explorează produsele</Link>
      </div>
    );

  return (
    <div>
      <h2 className="font-display text-4xl mb-6">Produse favorite</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => (
          <div key={p.slug} className="group relative" data-testid={`wishlist-item-${p.slug}`}>
            <Link to={`/produs/${p.slug}`}>
              <div className="aspect-[4/5] bg-stone overflow-hidden">
                <img src={p.image} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <p className="overline text-roast mt-3">{p.brand}</p>
              <h3 className="font-display text-xl">{p.name}</h3>
              <p className="text-sm">{RON(p.price)}</p>
            </Link>
            <button onClick={() => removeItem(p.slug)} className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-cream/85 hover:bg-roast hover:text-cream transition-colors" aria-label="Șterge">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="overline text-ink/50">{label}</label>
    <input type={type} value={value} onChange={onChange} data-testid={testid} className="mt-2 w-full bg-surface border border-line px-4 py-3 outline-none focus:border-roast transition-colors" />
  </div>
);
